import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { useGetOneExpeditionBo } from "@/api/expedition.api";
import { useGetCompanyBOV1 } from "@/api/company.api";
import { useGetCustoms } from "@/api/customs.api";

import { ActionDataBo } from "@/types/tableAction";
import {
  CustomReducedDto,
  GetAllCompanyDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { DocumentsUpdateModal } from "@/components/shared/modal/documentsUpdate.modal";
import { LogTimelineContent } from "@/components/shared/modal/logTimeline.modal";
import { BO_DETAIL_EXPEDITION } from "@/constants/fileCrud";
import { useIsMobile } from "@/hooks/use-mobile";

import { DrawerHeader } from "./drawerHeader";
import { DrawerFooter } from "./drawerFooter";
import { ExpeditionDetailsSection } from "./expeditionDetailsSection";
import { DocumentsSection } from "./documentsSection";
import { CommentAlert, DeliveryFromDateAlert } from "./expeditionAlerts";
import { getSortedDocuments } from "./expeditionDetail.utils";

type Props = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
  companyId: string;
  setActionData: (data: ActionDataBo) => void;
  isDeleted: boolean;
};

export const ExpeditionDetailDrawer = ({
  open,
  onClose,
  expeditionId,
  companyId,
  setActionData,
  isDeleted,
}: Props) => {
  const { data: expedition, isLoading: isLoadingExpedition } = useGetOneExpeditionBo(expeditionId);
  const { data: company, isLoading: isLoadingCompany } = useGetCompanyBOV1(companyId || "");
  const { data: customs = [] } = useGetCustoms();
  const isMobile = useIsMobile();
  const { t } = useTranslation(["boExpeditions", "common"]);

  const isLoading = isLoadingExpedition || isLoadingCompany;

  const content = (
    <>
      <div className="hidden">
        <DialogTitle>{t("expeditionDrawer.title")}</DialogTitle>
      </div>
      {isLoading || !expedition ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="animate-spin text-gray-500" />
        </div>
      ) : (
        <DrawerContent
          expedition={expedition}
          company={company}
          customs={customs}
          onClose={onClose}
          setActionData={setActionData}
          isDeleted={isDeleted}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="top-0 left-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 !p-0 sm:rounded-none">
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="flex h-dvh w-full flex-col gap-0 border-0 !p-0 md:!w-[740px] md:!max-w-[740px] lg:!w-[940px] lg:!max-w-[940px]"
      >
        {content}
      </SheetContent>
    </Sheet>
  );
};

type DrawerContentProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company?: GetAllCompanyDto;
  customs: CustomReducedDto[];
  onClose: () => void;
  setActionData: (data: ActionDataBo) => void;
  isDeleted: boolean;
};

const DrawerContent = ({
  expedition,
  company,
  customs,
  onClose,
  setActionData,
  isDeleted,
}: DrawerContentProps) => {
  const [showLogs, setShowLogs] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

  const triggerAction = (action: ActionDataBo["action"]) => {
    onClose();
    setActionData({ expedition, action });
  };

  const header = (
    <DrawerHeader
      expedition={expedition}
      companyName={company?.businessName}
      isDeleted={isDeleted}
      onShowLogs={() => setShowLogs(true)}
      onAction={triggerAction}
    />
  );

  if (showLogs) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-gray-50">
        {header}
        <LogsPanel expeditionId={expedition.id} onBack={() => setShowLogs(false)} />
      </div>
    );
  }

  return (
    <>
      {header}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-gray-50 p-5 md:overflow-hidden">
        <DeliveryFromDateAlert expedition={expedition} />
        <div className="grid grid-cols-1 items-stretch gap-4 md:min-h-0 md:flex-1 md:grid-cols-2">
          <div className="flex flex-col gap-4 md:h-full md:min-h-0">
            <ExpeditionDetailsSection
              expedition={expedition}
              company={company}
              customs={customs}
              isDeleted={isDeleted}
            />
          </div>
          <div className="flex flex-col gap-4 md:h-full md:min-h-0">
            <CommentAlert expedition={expedition} />
            <div className="md:min-h-0 md:flex-1">
              <DocumentsSection
                expedition={expedition}
                onEdit={() => setDocumentsModalOpen(true)}
              />
            </div>
          </div>
        </div>
      </div>
      <DrawerFooter
        isDeleted={isDeleted}
        isCustomQuotation={!!expedition.customQuotation}
        isBilled={!!expedition.expeditionBillingDetail?.billedDate}
        onAction={triggerAction}
      />
      <DocumentsUpdateModal
        open={documentsModalOpen}
        onClose={() => setDocumentsModalOpen(false)}
        expeditionId={expedition.id}
        documents={getSortedDocuments(expedition)}
        expeditionDocumentsId={
          expedition.expeditionDocuments && "id" in expedition.expeditionDocuments
            ? expedition.expeditionDocuments.id
            : undefined
        }
        invalidateQueryKey={["expedition", "bo", expedition.id]}
        isBo
        expedition={expedition}
        fileContextRules={BO_DETAIL_EXPEDITION}
      />
    </>
  );
};

const LogsPanel = ({ expeditionId, onBack }: { expeditionId: string; onBack: () => void }) => {
  const { t } = useTranslation(["boExpeditions", "common"]);

  return (
    <>
      <div className="border-border flex items-center justify-between border-b px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-700">{t("expeditionDrawer.logsTitle")}</h3>
        <Button variant="ghost" size="sm" onClick={onBack}>
          {t("common:actions.back")}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-[#F8FAFC] p-4">
        <LogTimelineContent
          entityType="EXPEDITION"
          entityId={expeditionId}
          className="h-full"
          vertical
        />
      </div>
    </>
  );
};
